/**
 * Centralized API Configuration
 * All backend endpoints and API utilities in one place
 */

// =============================================================================
// API BASE CONFIGURATION
// =============================================================================

/**
 * API Base URL - Change this for different environments
 * Development: http://localhost:8000
 * Production: https://api.devicelink.global
 */
// export const API_BASE_URL = 'http://127.0.0.1:8000';
// export const API_BASE_URL = 'http://178.104.31.192:3004'; // HTTP — blocked by browsers on HTTPS
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

/**
 * API Version - for future API versioning
 */
export const API_VERSION = 'v1';

/**
 * Request timeout in milliseconds
 */
export const REQUEST_TIMEOUT = 30000; // 30 seconds

// =============================================================================
// AUTHENTICATION ENDPOINTS
// =============================================================================

export const AUTH_ENDPOINTS = {
  // User Authentication
  LOGIN: '/api/auth/login/',
  REGISTER: '/api/auth/register/',
  LOGOUT: '/api/auth/logout/',

  // Password Reset Flow (3-step process)
  FORGOT_PASSWORD: '/api/auth/forgot-password/', // Step 1: Send OTP
  VERIFY_OTP: '/api/auth/verify-otp/', // Step 2: Verify OTP
  RESET_PASSWORD: '/api/auth/reset-password/', // Step 3: Reset Password

  // Token Management
  REFRESH_TOKEN: '/api/auth/refresh/',
  VERIFY_TOKEN: '/api/auth/verify/',
};

// =============================================================================
// USER MANAGEMENT ENDPOINTS
// =============================================================================

export const USER_ENDPOINTS = {
  // Profile Management
  PROFILE: '/api/user/profile/',
  UPDATE_PROFILE: '/api/user/profile/update/',

  // Account Settings
  CHANGE_PASSWORD: '/api/user/change-password/',
  DELETE_ACCOUNT: '/api/user/delete-account/',

  // User Preferences
  PREFERENCES: '/api/user/preferences/',
  UPDATE_PREFERENCES: '/api/user/preferences/update/',
};

// =============================================================================
// IMEI SERVICE ENDPOINTS
// =============================================================================

export const IMEI_ENDPOINTS = {
  // IMEI Operations
  CHECK: '/api/imei/check/',
  BULK_CHECK: '/api/imei/bulk-check/',

  // History & Reports
  HISTORY: '/api/imei/history/',
  HISTORY_DETAIL: '/api/imei/history/{id}/',
  EXPORT_HISTORY: '/api/imei/history/export/',

  // Reports
  GENERATE_REPORT: '/api/imei/report/generate/',
  DOWNLOAD_REPORT: '/api/imei/report/download/{id}/',
};

// =============================================================================
// ORDER MANAGEMENT ENDPOINTS
// =============================================================================

export const ORDER_ENDPOINTS = {
  // Order Operations
  LIST: '/api/orders/',
  DETAIL: '/api/orders/{id}/',
  CREATE: '/api/orders/create/',
  CANCEL: '/api/orders/{id}/cancel/',

  // Order Documents
  DOWNLOAD_RECEIPT: '/api/orders/{id}/receipt/',
  DOWNLOAD_INVOICE: '/api/orders/{id}/invoice/',
};

// =============================================================================
// SUBSCRIPTION ENDPOINTS
// =============================================================================

export const SUBSCRIPTION_ENDPOINTS = {
  // Current Subscription
  CURRENT: '/api/subscription/current/',
  USAGE: '/api/subscription/usage/',
  SUMMARY: '/api/subscription/summary/',

  // Plan Management
  PLANS: '/api/subscription/plans/',
  SUBSCRIBE: '/api/subscription/subscribe/',
  SUBSCRIBE_TO_PLAN: '/api/subscription/subscribe/{id}/',
  UPGRADE: '/api/subscription/upgrade/',
  CANCEL: '/api/subscription/cancel/',
  RENEW: '/api/subscription/renew/',

  // Billing
  BILLING_HISTORY: '/api/subscription/billing/',
  PAYMENT_METHODS: '/api/subscription/payment-methods/',
};

// =============================================================================
// INVOICE ENDPOINTS
// =============================================================================

export const INVOICE_ENDPOINTS = {
  // Invoice Management
  LIST: '/api/invoices/',
  DETAIL: '/api/invoices/{id}/',
  DOWNLOAD: '/api/invoices/{id}/download/',

  // Payment
  PAY: '/api/invoices/{id}/pay/',
  PAYMENT_STATUS: '/api/invoices/{id}/payment-status/',
};

// =============================================================================
// DASHBOARD & ANALYTICS ENDPOINTS
// =============================================================================

export const DASHBOARD_ENDPOINTS = {
  // Dashboard Data
  STATS: '/api/dashboard/stats/',
  USAGE: '/api/dashboard/usage/',
  RECENT_ACTIVITY: '/api/dashboard/activity/',

  // Analytics
  ANALYTICS: '/api/dashboard/analytics/',
  REPORTS: '/api/dashboard/reports/',
};

// =============================================================================
// ADMIN ENDPOINTS (Future Implementation)
// =============================================================================

export const ADMIN_ENDPOINTS = {
  // User Management
  USERS: '/api/admin/users/',
  USER_DETAIL: '/api/admin/users/{id}/',

  // System Management
  SYSTEM_STATS: '/api/admin/dashboard/stats/',
  SYSTEM_HEALTH: '/api/admin/system/health/',

  // Content Management
  NOTIFICATIONS: '/api/admin/notifications/',
  BROADCAST: '/api/admin/notifications/broadcast/',
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Build complete API URL
 * @param {string} endpoint - The endpoint path
 * @param {Object} params - URL parameters to replace (e.g., {id: 123})
 * @returns {string} Complete API URL
 */
export const buildApiUrl = (endpoint, params = {}) => {
  let url = `${API_BASE_URL}${endpoint}`;

  // Replace URL parameters like {id} with actual values
  Object.keys(params).forEach((key) => {
    url = url.replace(`{${key}}`, params[key]);
  });

  return url;
};

/**
 * Get authentication headers
 * @returns {Object} Headers object with Authorization token
 */
export const getAuthHeaders = () => {
  try {
    const token = localStorage.getItem('imei_checker_token');
    if (!token) return {};

    // Parse JSON if the token is stored as JSON string
    let parsedToken = token;
    try {
      // If it's a JSON string, parse it
      const parsed = JSON.parse(token);
      if (typeof parsed === 'string') {
        parsedToken = parsed;
      }
    } catch {
      // If parsing fails, use the token as-is
      parsedToken = token;
    }

    return parsedToken ? { Authorization: `Bearer ${parsedToken}` } : {};
  } catch (error) {
    console.error('Error getting auth headers:', error);
    return {};
  }
};

/**
 * Get default request headers
 * @returns {Object} Default headers for API requests
 */
export const getDefaultHeaders = () => ({
  'Content-Type': 'application/json',
  ...getAuthHeaders(),
});

/**
 * Handle API response
 * @param {Response} response - Fetch response object
 * @returns {Promise} Parsed response data
 */
export const handleApiResponse = async (response) => {
  const contentType = response.headers.get('content-type');

  let data;
  if (contentType && contentType.includes('application/json')) {
    data = await response.json();
  } else {
    data = await response.text();
  }

  if (!response.ok) {
    // Check for 401 Unauthorized
    if (response.status === 401) {
      // Trigger unauthorized callback if registered
      if (typeof apiConfig.onUnauthorized === 'function') {
        apiConfig.onUnauthorized();
      }
    }
    throw new Error(data.message || `HTTP error! status: ${response.status}`);
  }

  return data;
};

/**
 * Make API request with default configuration
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Request options
 * @param {Object} urlParams - URL parameters to replace
 * @returns {Promise} API response
 */
export const apiRequest = async (endpoint, options = {}, urlParams = {}) => {
  const url = buildApiUrl(endpoint, urlParams);

  const config = {
    timeout: REQUEST_TIMEOUT,
    ...options,
    headers: {
      ...getDefaultHeaders(),
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    return await handleApiResponse(response);
  } catch (error) {
    console.error(`API request failed: ${endpoint}`, error);
    throw error;
  }
};

// =============================================================================
// CONVENIENCE METHODS FOR COMMON OPERATIONS
// =============================================================================

/**
 * Authentication API calls
 */
export const authApi = {
  login: (credentials) =>
    apiRequest(AUTH_ENDPOINTS.LOGIN, {
      method: 'POST',
      body: JSON.stringify(credentials),
    }),

  register: (userData) =>
    apiRequest(AUTH_ENDPOINTS.REGISTER, {
      method: 'POST',
      body: JSON.stringify(userData),
    }),

  forgotPassword: (email) =>
    apiRequest(AUTH_ENDPOINTS.FORGOT_PASSWORD, {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  verifyOtp: (otpData) =>
    apiRequest(AUTH_ENDPOINTS.VERIFY_OTP, {
      method: 'POST',
      body: JSON.stringify(otpData),
    }),

  resetPassword: (resetData) =>
    apiRequest(AUTH_ENDPOINTS.RESET_PASSWORD, {
      method: 'POST',
      body: JSON.stringify(resetData),
    }),

  refreshToken: (refreshToken) =>
    apiRequest(AUTH_ENDPOINTS.REFRESH_TOKEN, {
      method: 'POST',
      body: JSON.stringify({ refresh: refreshToken }),
    }),

  verifyToken: () =>
    apiRequest(AUTH_ENDPOINTS.VERIFY_TOKEN, {
      method: 'GET',
    }),
};

/**
 * User profile API calls
 */
export const userApi = {
  getProfile: () => apiRequest(USER_ENDPOINTS.PROFILE, { method: 'GET' }),

  updateProfile: (profileData) =>
    apiRequest(USER_ENDPOINTS.UPDATE_PROFILE, {
      method: 'PUT',
      body: JSON.stringify(profileData),
    }),

  changePassword: (passwordData) =>
    apiRequest(USER_ENDPOINTS.CHANGE_PASSWORD, {
      method: 'POST',
      body: JSON.stringify(passwordData),
    }),
};

/**
 * IMEI service API calls
 */
export const imeiApi = {
  checkImei: (imeiData) =>
    apiRequest(IMEI_ENDPOINTS.CHECK, {
      method: 'POST',
      body: JSON.stringify(imeiData),
    }),

  getHistory: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString
      ? `${IMEI_ENDPOINTS.HISTORY}?${queryString}`
      : IMEI_ENDPOINTS.HISTORY;
    return apiRequest(endpoint, { method: 'GET' });
  },

  getHistoryDetail: (id) =>
    apiRequest(IMEI_ENDPOINTS.HISTORY_DETAIL, { method: 'GET' }, { id }),
};

/**
 * Dashboard API calls
 */
export const dashboardApi = {
  getStats: () => apiRequest(DASHBOARD_ENDPOINTS.STATS, { method: 'GET' }),

  getUsage: () => apiRequest(DASHBOARD_ENDPOINTS.USAGE, { method: 'GET' }),

  getRecentActivity: () =>
    apiRequest(DASHBOARD_ENDPOINTS.RECENT_ACTIVITY, { method: 'GET' }),
};

/**
 * Subscription API calls
 */
export const subscriptionApi = {
  getCurrent: () =>
    apiRequest(SUBSCRIPTION_ENDPOINTS.CURRENT, { method: 'GET' }),

  getPlans: () => apiRequest(SUBSCRIPTION_ENDPOINTS.PLANS, { method: 'GET' }),

  upgrade: (planData) =>
    apiRequest(SUBSCRIPTION_ENDPOINTS.UPGRADE, {
      method: 'POST',
      body: JSON.stringify(planData),
    }),
};

// =============================================================================
// ERROR HANDLING UTILITIES
// =============================================================================

/**
 * API Error class for better error handling
 */
export class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Handle API errors consistently
 * @param {Error} error - The error object
 * @returns {Object} Formatted error response
 */
export const handleApiError = (error) => {
  if (error instanceof ApiError) {
    return {
      success: false,
      message: error.message,
      status: error.status,
      data: error.data,
    };
  }

  return {
    success: false,
    message: error.message || 'An unexpected error occurred',
    status: 500,
  };
};

// =============================================================================
// ENVIRONMENT-SPECIFIC CONFIGURATIONS
// =============================================================================

/**
 * Environment configurations
 */
export const ENV_CONFIG = {
  development: {
    apiBaseUrl: 'http://localhost:8000',
    timeout: 30000,
    debug: true,
  },
  staging: {
    apiBaseUrl: 'https://staging-api.devicelink.global',
    timeout: 15000,
    debug: false,
  },
  production: {
    apiBaseUrl: 'https://api.devicelink.global',
    timeout: 10000,
    debug: false,
  },
};

/**
 * Get current environment configuration
 */
export const getCurrentEnvConfig = () => {
  const env = process.env.NODE_ENV || 'development';
  return ENV_CONFIG[env] || ENV_CONFIG.development;
};

// =============================================================================
// EXPORTS
// =============================================================================

// Export all endpoint collections
export const API_ENDPOINTS = {
  AUTH: AUTH_ENDPOINTS,
  USER: USER_ENDPOINTS,
  IMEI: IMEI_ENDPOINTS,
  ORDER: ORDER_ENDPOINTS,
  SUBSCRIPTION: SUBSCRIPTION_ENDPOINTS,
  INVOICE: INVOICE_ENDPOINTS,
  DASHBOARD: DASHBOARD_ENDPOINTS,
  ADMIN: ADMIN_ENDPOINTS,
};

// Export all API utilities
export const API_UTILS = {
  buildApiUrl,
  getAuthHeaders,
  getDefaultHeaders,
  handleApiResponse,
  apiRequest,
  handleApiError,
  ApiError,
};

// Export all API methods
export const API_METHODS = {
  auth: authApi,
  user: userApi,
  imei: imeiApi,
  dashboard: dashboardApi,
  subscription: subscriptionApi,
};

// Default export for easy importing
// Default export for easy importing
const apiConfig = {
  BASE_URL: API_BASE_URL,
  VERSION: API_VERSION,
  ENDPOINTS: API_ENDPOINTS,
  UTILS: API_UTILS,
  METHODS: API_METHODS,
  ENV_CONFIG: getCurrentEnvConfig(),
  onUnauthorized: null, // Callback for 401 responses
};

/**
 * Register a callback for 401 Unauthorized responses
 * @param {Function} callback - Function to call on 401 error
 */
export const registerUnauthorizedCallback = (callback) => {
  apiConfig.onUnauthorized = callback;
};

export default apiConfig;
